import React, { useState, useEffect, useCallback } from "react"
import { Modal, Spinner } from 'react-bootstrap'
import { Search, Sparkles } from "lucide-react"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import { fetchGenreSuggestions } from '../../../utils/keywordSuggestionApi'
import './SuggestedKeywordsModal.css'

const SuggestedGenresModal = ({
    isOpen,
    toggle,
    itemId,
    selectedCountry,
    selectedLanguage,
    onAddGenres,
    trackedGenres = [],
    keywordCount = 0,
    keywordCountLoading = false,
    keywordLimit = 'Unlimited',
    onUpgrade,
}) => {
    const [recommendedGenres, setRecommendedGenres] = useState([])
    const [selectedGenres, setSelectedGenres] = useState([])
    const [loading, setLoading] = useState(false)
    const [addingGenres, setAddingGenres] = useState(false)
    // Backend was still computing when polling timed out - show that instead
    // of the "No Recommendations" empty state, which implies a final result.
    const [stillProcessing, setStillProcessing] = useState(false)
    const lastFetchedItemId = React.useRef(null)
    // Bumped on close so a poll still running for a closed modal doesn't
    // write its result (or toast) into the next open.
    const fetchGeneration = React.useRef(0)

    const isUnlimited = keywordLimit === 'Unlimited'
    const limitNum = isUnlimited ? Infinity : parseInt(keywordLimit)
    const remainingSlots = isUnlimited ? Infinity : Math.max(0, limitNum - keywordCount)
    // While the real tracked-genre count is still loading (e.g. a fresh
    // browser session with no local cache yet), `keywordCount` defaults to 0
    // and would make it look like the full plan is available. Don't let that
    // stale/incomplete count decide whether a selection exceeds the limit.
    const wouldExceedLimit = !isUnlimited && !keywordCountLoading && selectedGenres.length > remainingSlots

    const fetchRecommendedGenres = useCallback(async () => {
        if (!itemId) return

        if (lastFetchedItemId.current === itemId) return
        lastFetchedItemId.current = itemId

        const generation = fetchGeneration.current
        setLoading(true)
        setStillProcessing(false)
        try {
            const data = await fetchGenreSuggestions(itemId)
            if (generation !== fetchGeneration.current) return

            if (data?.suggestions?.length > 0) {
                const genres = data.suggestions.map(item => item.genre)
                setRecommendedGenres(genres)
            } else if (data?.status === "processing") {
                lastFetchedItemId.current = null // Allow retry on reopen
                setStillProcessing(true)
                setRecommendedGenres([])
            } else {
                toastr.info("No recommended genres found for this app.")
                setRecommendedGenres([])
            }
        } catch (error) {
            if (generation !== fetchGeneration.current) return
            console.error("Error fetching recommended genres:", error)
            lastFetchedItemId.current = null // Allow retry on error
            toastr.error("Failed to fetch AI genre recommendations.")
            setRecommendedGenres([])
        } finally {
            if (generation === fetchGeneration.current) setLoading(false)
        }
    }, [itemId])

    useEffect(() => {
        if (isOpen && itemId) {
            fetchRecommendedGenres()
        }
        if (!isOpen) {
            setSelectedGenres([])
            lastFetchedItemId.current = null // Reset on close so it can refetch fresh if reopened
            fetchGeneration.current += 1
            setLoading(false)
        }
    }, [isOpen, itemId, fetchRecommendedGenres])

    // Case-insensitive check for tracked genres
    const isGenreTracked = useCallback((genre) => {
        if (!genre) return false
        const normalizedGenre = genre.toString().trim().toLowerCase()
        return trackedGenres.some(tg => tg && tg.toString().trim().toLowerCase() === normalizedGenre)
    }, [trackedGenres])

    // Memoize the list of genres that can be selected (not already tracked)
    const untrackedGenres = React.useMemo(() => {
        return recommendedGenres.filter(genre => !isGenreTracked(genre))
    }, [recommendedGenres, isGenreTracked])

    // Derived state for "Select All" checkbox
    const isAllSelected = untrackedGenres.length > 0 && selectedGenres.length === untrackedGenres.length

    const toggleGenreSelection = (genre) => {
        if (isGenreTracked(genre)) return

        setSelectedGenres(prev =>
            prev.includes(genre) ? prev.filter(g => g !== genre) : [...prev, genre]
        )
    }

    const toggleSelectAll = () => {
        if (isAllSelected) {
            setSelectedGenres([])
        } else {
            setSelectedGenres([...untrackedGenres])
        }
    }

    const handleAddSelectedGenres = async () => {
        if (selectedGenres.length === 0) {
            toastr.warning("Please select at least one genre.")
            return
        }

        if (wouldExceedLimit) {
            if (onUpgrade) onUpgrade()
            return
        }

        setAddingGenres(true)
        try {
            await onAddGenres(selectedGenres)
            setSelectedGenres([])
            toastr.success(`Successfully added ${selectedGenres.length} genres!`)
            toggle()
        } catch (error) {
            console.error("Error adding genres:", error)
            toastr.error("Failed to add selected genres.")
        } finally {
            setAddingGenres(false)
        }
    }

    return (
        <Modal
            show={isOpen}
            onHide={toggle}
            centered
            size="lg"
            className="modal_key suggested-keywords-modal"
        >
            <div className="suggested-keywords-content">
                <div className="modal-header-custom">
                    <div className="header-content">
                        <div className="header-icon ai-gradient-bg">
                            <Sparkles size={24} color="white" />
                        </div>
                        <div>
                            <h5>AI Genre Recommendations</h5>
                            <small className="text-muted">Smart suggestions based on your app's metadata</small>
                        </div>
                    </div>
                </div>

                {/* Plan limit banner */}
                {!isUnlimited && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 16px',
                        background: '#1a1f2e',
                        borderBottom: '1px solid #3A3B47',
                        fontSize: '13px',
                    }}>
                        <i className="fas fa-info-circle" style={{ color: '#9ca3af', flexShrink: 0 }} />
                        {keywordCountLoading ? (
                            <span style={{ color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Spinner animation="border" size="sm" style={{ color: '#9ca3af' }} />
                                Checking how many of your <strong style={{ color: '#fff' }}>{keywordLimit}</strong> genre slots are already in use…
                            </span>
                        ) : (
                            <span style={{ color: '#9ca3af' }}>
                                Your plan allows <strong style={{ color: '#fff' }}>{keywordLimit}</strong> genres —&nbsp;
                                <strong style={{ color: remainingSlots === 0 ? '#ef4444' : '#22c55e' }}>{remainingSlots}</strong> slot{remainingSlots !== 1 ? 's' : ''} remaining.
                                {remainingSlots === 0
                                    ? ' Selecting more will prompt an upgrade.'
                                    : ` Selecting more than ${remainingSlots} will prompt an upgrade.`}
                            </span>
                        )}
                    </div>
                )}

                <div className="modal-body-custom">
                    {loading ? (
                        <div className="loading-state">
                            <Spinner animation="border" style={{ color: '#F6255A' }} />
                            <p>Consulting KeyQuest AI...</p>
                        </div>
                    ) : stillProcessing ? (
                        <div className="empty-state">
                            <div className="empty-icon ai-icon-bg">
                                <Sparkles size={32} />
                            </div>
                            <h6>Still Generating</h6>
                            <p>KeyQuest AI is still analyzing this app. This can take a minute.</p>
                            <button
                                type="button"
                                className="btn btn-sm mt-2"
                                style={{ background: '#F6255A', color: '#fff' }}
                                onClick={fetchRecommendedGenres}
                            >
                                Check Again
                            </button>
                        </div>
                    ) : recommendedGenres.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon ai-icon-bg">
                                <Search size={32} />
                            </div>
                            <h6>No Recommendations</h6>
                            <p>We couldn't generate recommendations at this time.</p>
                        </div>
                    ) : (
                        <>
                            <div className="selection-controls">
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                                    <input
                                        type="checkbox"
                                        checked={isAllSelected}
                                        onChange={toggleSelectAll}
                                        style={{ width: 16, height: 16, accentColor: '#F6255A', cursor: 'pointer' }}
                                    />
                                    <span style={{ color: '#fff', fontSize: '14px' }}>Select All</span>
                                </label>
                                <span className="selection-count">
                                    {selectedGenres.length} of {untrackedGenres.length} available selected
                                </span>
                            </div>

                            <div className="keywords-list">
                                {recommendedGenres.map((genre, index) => {
                                    const isSelected = selectedGenres.includes(genre)
                                    const isTracked = isGenreTracked(genre)

                                    return (
                                        <div
                                            key={index}
                                            className={`keyword-item ${isSelected ? 'selected' : ''} ${isTracked ? 'tracked' : ''}`}
                                            onClick={() => !isTracked && toggleGenreSelection(genre)}
                                            style={{ cursor: isTracked ? 'not-allowed' : 'pointer' }}
                                        >
                                            <div className="keyword-content">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, userSelect: 'none', width: '100%' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        disabled={isTracked}
                                                        readOnly
                                                        style={{
                                                            width: 16,
                                                            height: 16,
                                                            accentColor: isTracked ? '#6b7280' : '#F6255A',
                                                            cursor: isTracked ? 'not-allowed' : 'pointer',
                                                            opacity: isTracked ? 0.5 : 1
                                                        }}
                                                    />
                                                    <div className="keyword-info">
                                                        <div className="keyword-name">{genre}</div>
                                                        {isTracked && (
                                                            <div className="tracked-indicator">
                                                                <span className="tracked-badge">Already Tracked</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </>
                    )}
                </div>

                <div className="modal-footer-custom">
                    <button
                        className="btn-cancel"
                        onClick={toggle}
                        disabled={addingGenres}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn-primary pink-accent-bg"
                        onClick={handleAddSelectedGenres}
                        disabled={selectedGenres.length === 0 || addingGenres || keywordCountLoading}
                        title={keywordCountLoading ? "Still checking your current genre count — try again in a moment" : undefined}
                        style={{ border: 'none', ...(wouldExceedLimit ? { background: '#F6255A' } : {}) }}
                    >
                        {addingGenres ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                Adding...
                            </>
                        ) : wouldExceedLimit ? (
                            <>
                                <i className="fas fa-lock me-2" style={{ fontSize: '12px' }} />
                                Upgrade to Add {selectedGenres.length} Genre{selectedGenres.length !== 1 ? 's' : ''}
                            </>
                        ) : selectedGenres.length > 0 ? (
                            <>Add {selectedGenres.length} Genre{selectedGenres.length !== 1 ? 's' : ''}</>
                        ) : (
                            <>Select Genres</>
                        )}
                    </button>
                </div>
            </div>
        </Modal>
    )
}

export default SuggestedGenresModal
